// src/components/HeroAtmosphere.jsx
//
// The hero's background atmosphere — designed to feel like
// a serious trader's terminal at rest. No grids, no orbs that
// scream for attention, no random gradient mesh.
//
// Three deliberate layers:
//   1. Asymmetric aurora — one soft green presence drifting off-axis,
//      with a faint cool counter-tone for depth. Slow weather, not animation.
//   2. Architectural horizon lines — ultra-thin reference lines with
//      sparse markers, like the bones of an institutional chart.
//   3. Atmospheric vertical gradient — subtle light fall on a dark screen.
//
// All elements are pointer-events:none and respect prefers-reduced-motion.
import React from 'react';
import { useScrollY } from '../hooks/useAnimations';

const G = '#00e5a0';

export default function HeroAtmosphere() {
  const scrollY = useScrollY();
  // Background drifts up faster than content — subtle depth parallax.
  // Capped so it never moves more than ~120px regardless of how far you scroll.
  const parallaxY = Math.min(120, scrollY * 0.25);

  return (
    <>
      {/* Layer 1: Atmospheric vertical wash.
          Top has a hair of warm green; bottom deepens. Mimics light
          falling on a terminal. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg,' +
            ' rgba(0,229,160,0.025) 0%,' +
            ' rgba(10,10,15,0) 18%,' +
            ' rgba(10,10,15,0) 70%,' +
            ' rgba(0,0,0,0.45) 100%)',
        }}
      />

      {/* Layer 2: Aurora — primary green presence.
          Off-axis (top-right area). Heavily blurred so it reads as
          atmosphere, not as a blob. Drifts in a slow elliptical loop.
          Outer wrapper handles scroll parallax; inner div handles the
          keyframe drift — composed cleanly without transform conflicts. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '8%',
          right: '-8%',
          width: 760,
          height: 760,
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate3d(0, ${parallaxY * -0.4}px, 0)`,
          willChange: 'transform',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side,' +
              ' rgba(0,229,160,0.16),' +
              ' rgba(0,229,160,0.06) 38%,' +
              ' rgba(0,229,160,0) 70%)',
            filter: 'blur(48px)',
            animation: 'aurora-drift 28s ease-in-out infinite',
            willChange: 'transform',
          }}
        />
      </div>

      {/* Layer 2b: Counter-glow — cool tone at the opposing diagonal.
          Adds depth, suggests dimensional space without colour bloom. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-12%',
          left: '-6%',
          width: 620,
          height: 620,
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate3d(0, ${parallaxY * 0.3}px, 0)`,
          willChange: 'transform',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background:
              'radial-gradient(closest-side,' +
              ' rgba(72,99,255,0.08),' +
              ' rgba(72,99,255,0.03) 40%,' +
              ' rgba(72,99,255,0) 72%)',
            filter: 'blur(56px)',
            animation: 'aurora-drift-2 34s ease-in-out infinite',
            willChange: 'transform',
          }}
        />
      </div>

      {/* Layer 3: Architectural horizon lines.
          Two reference lines at the lower thirds, with sparse markers —
          the bones of a clean chart. Static. Adds composition. */}
      <HorizonLine top="34%" />
      <HorizonLine top="66%" intensity={0.65} />

      {/* Layer 4: Edge vignette — quiet focus toward centre. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 95% 70% at center,' +
            ' transparent 55%,' +
            ' rgba(0,0,0,0.55) 100%)',
        }}
      />
    </>
  );
}

function HorizonLine({ top = '50%', intensity = 1 }) {
  const baseAlpha = 0.045 * intensity;
  const dotAlpha  = 0.18  * intensity;

  // 6 evenly-spaced dot markers spanning the line
  const dots = [0.08, 0.235, 0.39, 0.545, 0.7, 0.855, 0.99];

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        height: 1,
        zIndex: 1,
        pointerEvents: 'none',
        background: `linear-gradient(90deg, transparent, rgba(255,255,255,${baseAlpha}) 18%, rgba(255,255,255,${baseAlpha}) 82%, transparent)`,
      }}
    >
      {dots.map((x, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            left: `${x * 100}%`,
            top: -1,
            width: 3,
            height: 3,
            borderRadius: '50%',
            background: `rgba(0,229,160,${dotAlpha})`,
            transform: 'translateX(-50%)',
          }}
        />
      ))}
    </div>
  );
}
